/**
 * Swing damage by weapon tier — the client-side mirror of the server's
 * `backend/src/game/crafting/gear.ts weaponDamage()`.
 *
 * Mobs and animals are simulated on the client (like movement), so the client is
 * what decides when something dies; the server still owns every reward, so the
 * worst a tampered client does is claim a kill faster, not mint loot. Keeping the
 * two ladders identical means the numbers a player sees floating off a mob match
 * the ones the server used when it priced the fight.
 *
 * Tier scale: 1=Wood 2=Stone 3=Copper 4=Iron 5=Gold 6=Mythril.
 */

/** Bare-handed / no-weapon baseline, so a swing always does something. */
export const FIST_DAMAGE = 3;

/**
 * Highest tool/weapon tier. Mirrors TOP_TIER in backend crafting/crafting.config
 * — it moved from 6 to 9 when emerald, amber and ruby joined the ladder, and a
 * client still capping at 6 would silently under-report every hit above mythril.
 */
export const TOP_TIER = 9;

/** Damage a sword of this tier adds on top of the fist. Mythril = +18. */
export function weaponDamage(tier: number): number {
  return Math.max(0, Math.min(TOP_TIER, tier)) * 3;
}

/**
 * Damage a bow of this tier adds. Below the sword at equal tier on purpose — the
 * bow buys reach, not raw damage (mirrors the server's `bowDamage`).
 */
export function bowDamage(tier: number): number {
  return Math.max(0, Math.min(TOP_TIER, tier)) * 2;
}

/** How far an arrow reaches, in world pixels. Flat — the tier buys damage, not range. */
export const BOW_RANGE = 200;

/** Total damage an arrow lands with the given bow tier. */
export function shotDamage(bowTier: number): number {
  return FIST_DAMAGE + bowDamage(bowTier);
}

/** Total damage a hero swing lands with the given sword tier. */
export function swingDamage(swordTier: number): number {
  return FIST_DAMAGE + weaponDamage(swordTier);
}

/**
 * Hunting an animal with a sword is overkill, so the same swing lands softer than
 * it does on a mob — animals have far less HP and one-shotting every deer at
 * tier 1 would make the food loop trivial.
 */
export function huntDamage(swordTier: number): number {
  return Math.max(2, Math.round(swingDamage(swordTier) * 0.6));
}

/**
 * Hits a mineable node takes at its tier. A big boulder wants several swings
 * (§15: 3-4 rounds), and a better pickaxe shortens that rather than skipping it.
 */
export function nodeHits(base: number, pickaxeTier: number): number {
  return Math.max(1, base - Math.floor(Math.max(1, pickaxeTier) / 3));
}
